import { useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  ROOM_TYPES,
  isRoomType,
  type Room,
  type RoomCreateRequest,
  type RoomType,
  type RoomUpdateRequest,
} from "../types";

type RoomFormDialogProps = (
  | {
      mode: "create";
      room?: undefined;
      freshRoom?: undefined;
      onCreate: (data: RoomCreateRequest) => Promise<void> | void;
      onUpdate?: undefined;
    }
  | {
      mode: "edit";
      room: Room;
      /**
       * Latest cached version of `room`, refetched while the dialog is open.
       * If its `updatedAt` differs from the originally-opened room we show a
       * non-blocking "updated elsewhere" banner so the user can reload. Optional;
       * falls back to `room` when absent.
       */
      freshRoom?: Room;
      onCreate?: undefined;
      onUpdate: (data: RoomUpdateRequest) => Promise<void> | void;
    }
) & {
  open: boolean;
  onClose: () => void;
  isLoading?: boolean;
  error?: string | null;
};

/** Trim then coerce empty -> null. */
function nullableString(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Sub-dialog for creating or editing a single Room. Owns its own form state so
 * the parent `EditRoomsDialog` can remain a thin orchestrator.
 *
 * The `capacity` field is rendered as a text input rather than `type="number"`
 * so we can faithfully distinguish "empty" (=> null) from "0" and avoid the
 * scroll-to-change behavior of native number inputs. Validation parses to a
 * non-negative integer on submit; invalid entries surface as form error.
 */
export function RoomFormDialog(props: RoomFormDialogProps) {
  const { open, onClose, isLoading = false, error, mode, room } = props;

  // Snapshot updatedAt at mount for the lost-update detector.
  const [openedUpdatedAt] = useState<string | null>(room?.updatedAt ?? null);
  const [name, setName] = useState(room?.name ?? "");
  const [type, setType] = useState<RoomType>(room?.type ?? "Internal");
  const [capacity, setCapacity] = useState(
    room?.capacity != null ? String(room.capacity) : "",
  );
  const [notes, setNotes] = useState(room?.notes ?? "");
  const [localError, setLocalError] = useState<string | null>(null);
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false);

  const freshRoom: Room | null =
    props.mode === "edit" ? (props.freshRoom ?? props.room) : null;
  const isStale =
    mode === "edit" &&
    !staleBannerDismissed &&
    freshRoom != null &&
    openedUpdatedAt != null &&
    freshRoom.updatedAt !== openedUpdatedAt;

  const handleReloadFromServer = () => {
    if (!freshRoom) return;
    setName(freshRoom.name);
    setType(freshRoom.type);
    setCapacity(freshRoom.capacity != null ? String(freshRoom.capacity) : "");
    setNotes(freshRoom.notes ?? "");
    setStaleBannerDismissed(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    let parsedCapacity: number | null = null;
    if (capacity.trim().length > 0) {
      const n = Number(capacity);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
        setLocalError("Capacity must be a non-negative whole number.");
        return;
      }
      parsedCapacity = n;
    }

    const payload: RoomCreateRequest = {
      name: name.trim(),
      type,
      capacity: parsedCapacity,
      notes: nullableString(notes),
    };

    if (props.mode === "create") {
      await props.onCreate(payload);
      return;
    }

    await props.onUpdate(payload);
  };

  const displayError = localError ?? error ?? null;
  const title = mode === "create" ? "Add room" : "Edit room";
  const submitLabel = mode === "create" ? "Create room" : "Save changes";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      testId="room-form-dialog"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {isStale && (
          <div
            role="status"
            data-testid="room-form-stale-banner"
            className="flex items-center justify-between gap-3 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs"
          >
            <span>This room was updated elsewhere.</span>
            <button
              type="button"
              onClick={handleReloadFromServer}
              data-testid="room-form-stale-reload"
              className="underline hover:no-underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            >
              Reload latest
            </button>
          </div>
        )}
        {displayError && (
          <div
            role="alert"
            data-testid="room-form-error"
            className="px-3 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-xs"
          >
            {displayError}
          </div>
        )}
        <div>
          <Label htmlFor="rf-name">Name</Label>
          <Input
            id="rf-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="room-name-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="rf-type">Type</Label>
            <select
              id="rf-type"
              value={type}
              onChange={(e) => {
                const v = e.target.value;
                if (isRoomType(v)) setType(v);
              }}
              data-testid="room-type-select"
              className="w-full bg-page border border-border-subtle rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand"
            >
              {ROOM_TYPES.map((rt) => (
                <option key={rt} value={rt}>
                  {rt}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="rf-capacity">
              Capacity{" "}
              <span className="text-text-faint font-normal">
                {type === "Internal" ? "(required at run time)" : "(optional)"}
              </span>
            </Label>
            <Input
              id="rf-capacity"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="e.g. 30"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              data-testid="room-capacity-input"
            />
          </div>
        </div>
        <div>
          <Label htmlFor="rf-notes">Notes</Label>
          <Input
            id="rf-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="room-notes-input"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            data-testid="room-form-cancel"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isLoading}
            data-testid="room-form-submit"
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
